import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Pencil, Plus, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  captureCommonToolReturnContext,
  navigateCommonToolReturn,
  resolveCommonToolReturnLabel,
} from "@/lib/equipoNavigation";
import { PageHeader } from "@/pages/layoutChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PasswordInput } from "@/components/ui/password-input";
import { CONFIGURACION_DESPACHO_PATH } from "@/modules/config/configuracionRoutes";
import {
  MSG_LICENSE_USER_LIMIT,
  computeUserSlotStats,
  isWorkspaceUserActive,
} from "@/lib/workspaceTeamLimits";
import { getWorkspaceRoleLabel } from "@/lib/userRoles";
import { useEntitlements } from "@/modules/useEntitlements";
import { AUXILIARY_PHONE_HELP } from "@/lib/auxiliaryPhone";
import {
  AUXILIARY_DESPACHO_ROLES,
  canManageWorkspaceTeam,
  despachoRoleHint,
  despachoRoleLabel,
  workspaceRoleFromDespachoRole,
} from "@/lib/auxiliaryRoleGuide";
import AuxiliaryRolesGuide from "@/components/equipo/AuxiliaryRolesGuide";
import TeamRolePermissionsPanel from "@/components/equipo/TeamRolePermissionsPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function SlotStat({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-center">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-[#0F2D5C] mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

/**
 * Gestión simple de usuarios/auxiliares del espacio (sin módulo Gestión Despacho).
 * @param {{ embedded?: boolean, onRequestLicenseExpansion?: () => void }} [props]
 */
export default function EquipoBasicoUsuarios({ embedded = false, onRequestLicenseExpansion = null }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { systemInfo, loading: infoLoading } = useEntitlements();
  const isBuyerTenantAdmin = Boolean(user?.is_buyer_tenant && user?.role === "buyer_admin");
  const canManage = canManageWorkspaceTeam(user);
  const returnTargetRef = useRef(captureCommonToolReturnContext(location));
  const returnLabel = resolveCommonToolReturnLabel(location);

  const [equipoSection, setEquipoSection] = useState("usuarios");
  const [listScope, setListScope] = useState("active");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [busyCreate, setBusyCreate] = useState(false);
  const [busyAction, setBusyAction] = useState(false);
  const [newU, setNewU] = useState({ email: "", name: "", password: "", phone: "", despacho_role: "auxiliar" });
  const [editU, setEditU] = useState({ name: "", password: "", phone: "", despacho_role: "auxiliar" });

  const loadUsers = useCallback(async () => {
    if (!canManage) {
      setUsers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // En permisos necesitamos activos e inactivos para poder reactivar.
      const includeInactive = listScope === "inactive" || equipoSection === "permisos";
      const { data } = await api.get("/admin/users", {
        params: includeInactive ? { include_inactive: true } : {},
      });
      setUsers(Array.isArray(data) ? data : []);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [canManage, listScope, equipoSection]);

  useEffect(() => {
    returnTargetRef.current = captureCommonToolReturnContext(location);
  }, [location.state?.returnTo, location.search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const visibleUsers = useMemo(() => {
    if (listScope === "inactive") {
      return users.filter(
        (u) => !u.is_admin_owner && !u.is_buyer_tenant && !isWorkspaceUserActive(u),
      );
    }
    return users;
  }, [users, listScope]);

  const slotStats = useMemo(() => computeUserSlotStats(systemInfo, users), [systemInfo, users]);

  const handleLeave = () => {
    if (embedded) {
      setCreateOpen(false);
      setEditOpen(false);
      return;
    }
    navigateCommonToolReturn(navigate, location, returnTargetRef.current);
  };

  const submitCreate = async () => {
    if (!newU.email?.trim() || !newU.name?.trim()) {
      toast.error("Correo y nombre son obligatorios");
      return;
    }
    if (!newU.password || newU.password.length < 4) {
      toast.error("La contraseña debe tener al menos 4 caracteres");
      return;
    }
    if (slotStats.atLimit) {
      toast.error(MSG_LICENSE_USER_LIMIT);
      return;
    }
    setBusyCreate(true);
    try {
      const { data } = await api.post("/admin/users/create", {
        email: newU.email.trim().toLowerCase(),
        name: newU.name.trim(),
        // Comprador: solo role=user; el perfil (incl. consulta) va en despacho_role.
        role: isBuyerTenantAdmin ? "user" : workspaceRoleFromDespachoRole(newU.despacho_role),
        password: newU.password,
        permitted_clients: [],
        phone: newU.phone.trim() || undefined,
        mobile_phone: newU.phone.trim() || undefined,
        despacho_role: newU.despacho_role || "auxiliar",
      });
      if (data?.reactivated) {
        toast.success("Usuario reactivado correctamente");
      } else if (data?.linked) {
        toast.success("Usuario vinculado como auxiliar");
      } else {
        toast.success("Auxiliar creado correctamente");
      }
      setCreateOpen(false);
      setNewU({ email: "", name: "", password: "", phone: "", despacho_role: "auxiliar" });
      await loadUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo crear el usuario");
    } finally {
      setBusyCreate(false);
    }
  };

  const openEdit = (target) => {
    setEditTarget(target);
    setEditU({
      name: target.name || "",
      password: "",
      phone: target.phone || target.mobile_phone || "",
      despacho_role: target.despacho_role || "auxiliar",
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editTarget?.id) return;
    if (!editU.name?.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    if (editU.password?.length > 0 && editU.password.length < 4) {
      toast.error("La contraseña debe tener al menos 4 caracteres");
      return;
    }
    if (editU.password?.length >= 4) {
      if (
        !window.confirm(
          `¿Restablecer la contraseña de ${editTarget.email}? El auxiliar deberá usar la nueva clave al iniciar sesión.`,
        )
      ) {
        return;
      }
    }
    setBusyAction(true);
    try {
      const nextRole = editU.despacho_role || "auxiliar";
      const payload = {
        user_id: editTarget.id,
        new_name: editU.name.trim(),
        phone: editU.phone.trim(),
        mobile_phone: editU.phone.trim(),
        despacho_role: nextRole,
      };
      if (!isBuyerTenantAdmin) {
        payload.new_role = workspaceRoleFromDespachoRole(nextRole);
      }
      if (editU.password?.length >= 4) {
        payload.new_password = editU.password;
      }
      await api.post("/admin/users/reset", payload);
      toast.success("Usuario actualizado");
      setEditOpen(false);
      setEditTarget(null);
      await loadUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo actualizar");
    } finally {
      setBusyAction(false);
    }
  };

  const toggleUserStatus = async (target, activate) => {
    if (target.is_admin_owner || target.is_buyer_tenant) return;
    if (
      !window.confirm(
        activate
          ? `¿Reactivar a ${target.name || target.email}? Volverá a poder entrar como auxiliar.`
          : `¿Desactivar a ${target.name || target.email}? Conservará historial; podrá reactivarse después.`,
      )
    ) {
      return;
    }
    setBusyAction(true);
    try {
      await api.post("/admin/users/reset", {
        user_id: target.id,
        workspace_status: activate ? "active" : "inactive",
      });
      toast.success(activate ? "Usuario reactivado" : "Usuario desactivado");
      await loadUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al actualizar");
    } finally {
      setBusyAction(false);
    }
  };

  const changeUserRole = async (target, nextDespachoRole) => {
    if (!target?.id || target.is_admin_owner || target.is_buyer_tenant) return;
    const current = target.despacho_role || "auxiliar";
    if (current === nextDespachoRole) return;
    setBusyAction(true);
    try {
      const payload = {
        user_id: target.id,
        despacho_role: nextDespachoRole,
      };
      if (!isBuyerTenantAdmin) {
        payload.new_role = workspaceRoleFromDespachoRole(nextDespachoRole);
      }
      await api.post("/admin/users/reset", payload);
      toast.success(`Perfil actualizado: ${despachoRoleLabel(nextDespachoRole)}`);
      await loadUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cambiar el perfil");
    } finally {
      setBusyAction(false);
    }
  };

  if (!canManage) {
    if (embedded) {
      return (
        <p className="text-sm text-slate-600 m-0" data-testid="equipo-basico-usuarios-embedded-denied">
          Solo el titular o administrador comprador puede administrar el equipo.
        </p>
      );
    }
    return (
      <div className="p-8 max-w-3xl">
        <PageHeader title="Usuarios y auxiliares" subtitle="Acceso restringido al titular o administrador del espacio." />
        <p className="text-sm text-slate-600 mt-4">Solo el titular o administrador comprador puede administrar el equipo.</p>
      </div>
    );
  }

  const wrapClass = embedded ? "space-y-4" : "p-8 max-w-4xl space-y-6";
  const testId = embedded ? "equipo-basico-usuarios-embedded" : "equipo-basico-usuarios-page";

  return (
    <div className={wrapClass} data-testid={testId}>
      {!embedded ? (
        <>
          <div className="flex flex-wrap items-start gap-3 justify-between">
            <PageHeader
              title="Usuarios y auxiliares"
              subtitle="Equipo básico con acceso al sistema. No requiere el módulo Gestión Despacho."
            />
            <Button
              type="button"
              variant="outline"
              className="h-9 shrink-0"
              onClick={handleLeave}
              data-testid="equipo-usuarios-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              {returnLabel}
            </Button>
          </div>
        </>
      ) : null}

      <Tabs
        value={equipoSection}
        onValueChange={setEquipoSection}
        className="space-y-4"
        data-testid="equipo-section-tabs"
      >
        <TabsList
          className="h-auto min-h-9 flex flex-wrap justify-start gap-1 rounded-sm bg-white border border-slate-200 p-1 w-full sm:w-auto"
          data-testid="equipo-section-tabs-list"
        >
          <TabsTrigger
            value="usuarios"
            className="rounded-sm text-xs px-3 py-1.5 data-[state=active]:bg-[#0F2D5C] data-[state=active]:text-white"
            data-testid="equipo-tab-usuarios"
          >
            Usuarios
          </TabsTrigger>
          <TabsTrigger
            value="permisos"
            className="rounded-sm text-xs px-3 py-1.5 data-[state=active]:bg-[#0F2D5C] data-[state=active]:text-white"
            data-testid="equipo-tab-permisos"
          >
            Permisos Auxiliar vs Administrador
          </TabsTrigger>
        </TabsList>

        <TabsContent value="usuarios" className="mt-0 space-y-4 focus-visible:outline-none">
          <div className="grid grid-cols-3 gap-3 max-w-md" data-testid="equipo-user-slot-stats">
            <SlotStat label="Autorizados" value={slotStats.authorized} />
            <SlotStat label="Activos" value={slotStats.active} />
            <SlotStat label="Disponibles" value={slotStats.available} />
          </div>

          {slotStats.atLimit ? (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950"
              data-testid="equipo-user-limit-notice"
            >
              <p className="m-0 font-medium">{MSG_LICENSE_USER_LIMIT}</p>
              {onRequestLicenseExpansion ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2 h-8 text-xs"
                  onClick={onRequestLicenseExpansion}
                >
                  Solicitar ampliación
                </Button>
              ) : (
                <Button asChild variant="outline" size="sm" className="mt-2 h-8 text-xs">
                  <Link to={`${CONFIGURACION_DESPACHO_PATH}?solicitar_licencia=1`}>Solicitar ampliación</Link>
                </Button>
              )}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2 justify-between items-center">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs text-slate-600 m-0">
                {listScope === "active"
                  ? "Solo auxiliares activos del espacio de trabajo actual."
                  : "Histórico: auxiliares inactivos o suspendidos del espacio de trabajo."}
              </p>
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-[11px]">
                <button
                  type="button"
                  className={`px-2.5 py-1 ${listScope === "active" ? "bg-[#0F2D5C] text-white" : "bg-white text-slate-700"}`}
                  onClick={() => setListScope("active")}
                  data-testid="equipo-list-scope-active"
                >
                  Activos
                </button>
                <button
                  type="button"
                  className={`px-2.5 py-1 ${listScope === "inactive" ? "bg-[#0F2D5C] text-white" : "bg-white text-slate-700"}`}
                  onClick={() => setListScope("inactive")}
                  data-testid="equipo-list-scope-inactive"
                >
                  Inactivos / Histórico
                </button>
              </div>
            </div>
            <Button
              type="button"
              className="h-9 bg-[#0F2D5C] hover:bg-[#0a2247]"
              disabled={!slotStats.canAddUser || busyAction || infoLoading}
              onClick={() => setCreateOpen(true)}
              data-testid="equipo-add-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-1.5" />
              Agregar auxiliar
            </Button>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            {loading ? (
              <div className="py-10 text-center text-sm text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="px-3 py-2 font-semibold text-slate-600">Nombre</th>
                    <th className="px-3 py-2 font-semibold text-slate-600">Correo</th>
                    <th className="px-3 py-2 font-semibold text-slate-600">Teléfono</th>
                    <th className="px-3 py-2 font-semibold text-slate-600">Rol</th>
                    <th className="px-3 py-2 font-semibold text-slate-600">Estado</th>
                    <th className="px-3 py-2 font-semibold text-slate-600 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleUsers.map((u) => {
                    const active = isWorkspaceUserActive(u);
                    const locked = u.is_admin_owner || u.is_buyer_tenant;
                    return (
                      <tr key={u.id} className="border-t border-slate-100">
                        <td className="px-3 py-2">{u.name || "—"}</td>
                        <td className="px-3 py-2 font-mono text-[11px]">{u.email}</td>
                        <td className="px-3 py-2 font-mono text-[11px]">{u.phone || u.mobile_phone || "—"}</td>
                        <td className="px-3 py-2">
                          {u.is_admin_owner || u.is_buyer_tenant
                            ? getWorkspaceRoleLabel(u.role, u)
                            : despachoRoleLabel(u.despacho_role || "auxiliar")}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold ${
                              active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"
                            }`}
                          >
                            {active ? "Activo" : "Inactivo"}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {!locked ? (
                            <div className="inline-flex flex-wrap gap-1 justify-end">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 text-[11px]"
                                disabled={busyAction}
                                onClick={() => openEdit(u)}
                                data-testid={`equipo-edit-user-${u.id}`}
                              >
                                <Pencil className="w-3 h-3 mr-1" />
                                Editar
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 text-[11px]"
                                disabled={busyAction}
                                onClick={() => toggleUserStatus(u, !active)}
                                data-testid={`equipo-toggle-user-${u.id}`}
                              >
                                {active ? "Desactivar" : "Reactivar"}
                              </Button>
                            </div>
                          ) : (
                            <span className="text-[10px] text-slate-400">Titular</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <AuxiliaryRolesGuide />
        </TabsContent>

        <TabsContent value="permisos" className="mt-0 focus-visible:outline-none">
          <TeamRolePermissionsPanel
            users={users}
            loading={loading}
            busy={busyAction}
            canEditMatrix={Boolean(user?.is_admin_owner || isBuyerTenantAdmin)}
            onToggleAccess={toggleUserStatus}
            onChangeRole={changeUserRole}
          />
        </TabsContent>
      </Tabs>

      {!embedded ? (
        <p className="text-[11px] text-slate-500 m-0 flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" />
          Para operación avanzada (asignaciones, pendientes, bandeja WhatsApp) use{" "}
          <Link to="/despacho/inicio" className="text-[#0F2D5C] font-semibold underline">
            Gestión Despacho
          </Link>
          .
        </p>
      ) : null}

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open && embedded) return;
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-4 h-4" /> Nuevo auxiliar
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nombre</Label>
              <Input
                value={newU.name}
                onChange={(e) => setNewU((s) => ({ ...s, name: e.target.value }))}
                className="mt-1 h-9"
              />
            </div>
            <div>
              <Label className="text-xs">Correo</Label>
              <Input
                type="email"
                value={newU.email}
                onChange={(e) => setNewU((s) => ({ ...s, email: e.target.value }))}
                className="mt-1 h-9 font-mono"
                data-testid="equipo-create-email"
              />
            </div>
            <div>
              <Label className="text-xs">Teléfono / celular</Label>
              <Input
                type="tel"
                value={newU.phone}
                onChange={(e) => setNewU((s) => ({ ...s, phone: e.target.value }))}
                className="mt-1 h-9 font-mono"
                placeholder="8136475360"
                data-testid="equipo-create-phone"
              />
              <p className="text-[10px] text-slate-500 mt-1 m-0">{AUXILIARY_PHONE_HELP}</p>
            </div>
            <div>
              <Label className="text-xs">Perfil en despacho</Label>
              <Select
                value={newU.despacho_role}
                onValueChange={(value) => setNewU((s) => ({ ...s, despacho_role: value }))}
              >
                <SelectTrigger className="mt-1 h-9" data-testid="equipo-create-role">
                  <SelectValue placeholder="Rol" />
                </SelectTrigger>
                <SelectContent>
                  {AUXILIARY_DESPACHO_ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500 mt-1 m-0">{despachoRoleHint(newU.despacho_role)}</p>
            </div>
            <div>
              <Label className="text-xs">Contraseña inicial</Label>
              <PasswordInput
                value={newU.password}
                onChange={(e) => setNewU((s) => ({ ...s, password: e.target.value }))}
                className="mt-1 h-9"
                autoComplete="new-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              data-testid="equipo-create-cancel-btn"
            >
              Cancelar
            </Button>
            <Button onClick={submitCreate} disabled={busyCreate} className="bg-[#3DA873] hover:bg-[#3DA873]/90">
              {busyCreate ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear auxiliar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar auxiliar</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Correo (no editable)</Label>
              <Input
                type="email"
                value={editTarget?.email || ""}
                readOnly
                disabled
                className="mt-1 h-9 font-mono bg-slate-50"
                data-testid="equipo-edit-email-readonly"
              />
            </div>
            <div>
              <Label className="text-xs">Nombre</Label>
              <Input
                value={editU.name}
                onChange={(e) => setEditU((s) => ({ ...s, name: e.target.value }))}
                className="mt-1 h-9"
              />
            </div>
            <div>
              <Label className="text-xs">Teléfono / celular</Label>
              <Input
                type="tel"
                value={editU.phone}
                onChange={(e) => setEditU((s) => ({ ...s, phone: e.target.value }))}
                className="mt-1 h-9 font-mono"
                data-testid="equipo-edit-phone"
              />
              <p className="text-[10px] text-slate-500 mt-1 m-0">{AUXILIARY_PHONE_HELP}</p>
            </div>
            <div>
              <Label className="text-xs">Perfil en despacho</Label>
              <Select
                value={editU.despacho_role}
                onValueChange={(value) => setEditU((s) => ({ ...s, despacho_role: value }))}
              >
                <SelectTrigger className="mt-1 h-9" data-testid="equipo-edit-role">
                  <SelectValue placeholder="Rol" />
                </SelectTrigger>
                <SelectContent>
                  {AUXILIARY_DESPACHO_ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500 mt-1 m-0">{despachoRoleHint(editU.despacho_role)}</p>
            </div>
            <div>
              <Label className="text-xs">Nueva contraseña (opcional)</Label>
              <PasswordInput
                value={editU.password}
                onChange={(e) => setEditU((s) => ({ ...s, password: e.target.value }))}
                className="mt-1 h-9"
                autoComplete="new-password"
                data-testid="equipo-edit-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="equipo-edit-cancel-btn">
              Cancelar
            </Button>
            <Button onClick={submitEdit} disabled={busyAction} data-testid="equipo-edit-save-btn">
              {busyAction ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
